export const ScbBillingFrequency = { Monthly: 1, Quarterly: 2, Yearly: 3 } as const;

export const ScbDealStatus = {
  Draft: 0,
  Locked: 1,
  Installing: 2,
  Live: 3,
  Suspended: 4,
  Closed: 5,
  Cancelled: 6,
} as const;

export const ScbInstallationStatus = {
  Planned: 0,
  InProgress: 1,
  Completed: 2,
  Cancelled: 3,
} as const;

export const ScbServiceContractStatus = {
  Draft: 0,
  Active: 1,
  PastDue: 2,
  Grace: 3,
  Suspended: 4,
  Closed: 5,
} as const;

export const ScbBillingPlanLineStatus = {
  Scheduled: 0,
  Invoiced: 1,
  Sent: 2,
  PartiallyPaid: 3,
  Paid: 4,
  Overdue: 5,
  Skipped: 6,
  Cancelled: 7,
} as const;

export const ScbBillingRunStatus = {
  Running: 0,
  Completed: 1,
  Failed: 2,
} as const;

export const ScbBillingRunLineStatus = {
  Pending: 0,
  Success: 1,
  Failed: 2,
} as const;

export const ScbNotificationChannel = {
  None: 0,
  Email: 1,
  WhatsApp: 2,
  Manual: 3,
} as const;

export const ScbPaymentProofStatus = {
  Pending: 0,
  Approved: 1,
  Rejected: 2,
} as const;

/** `GET /api/scb/payment-proofs` row classification. */
export const ScbPaymentProofRowKind = {
  PayInvoiceOnly: 1,
  PaymentProofSubmitted: 2,
} as const;

export const ScbCostCategory = {
  Install: 0,
  Recurring: 1,
  Commission: 2,
  Other: 3,
} as const;

/** Profile `billing.severity` for tenant soft-lock banner. */
export const ScbBillingSeverity = {
  None: 0,
  DueSoon: 1,
  Grace: 2,
  Suspended: 3,
} as const;

/** `POST /api/scb/my-billing/pay-invoice` paymentMethod */
export const ScbMyBillingPaymentMethod = {
  Cash: 1,
  Bank: 2,
} as const;

/** Tenant pending-invoice `tenantPaymentStatus` (list / detail / pay response). */
export const ScbTenantPaymentStatus = {
  NotPaid: 0,
  WaitingForProof: 1,
  AwaitingPlatformConfirmation: 2,
} as const;

export const SCB_TENANT_PAYMENT_STATUS_LABEL: Record<number, string> = {
  [ScbTenantPaymentStatus.NotPaid]: 'Not paid',
  [ScbTenantPaymentStatus.WaitingForProof]: 'Waiting for proof',
  [ScbTenantPaymentStatus.AwaitingPlatformConfirmation]: 'Awaiting confirmation',
};

export interface ScbOption {
  value: number;
  label: string;
}

export const SCB_BILLING_FREQUENCY_OPTIONS: ScbOption[] = [
  { value: ScbBillingFrequency.Monthly, label: 'Monthly' },
  { value: ScbBillingFrequency.Quarterly, label: 'Quarterly' },
  { value: ScbBillingFrequency.Yearly, label: 'Yearly' },
];

export const SCB_DEAL_STATUS_OPTIONS: ScbOption[] = [
  { value: ScbDealStatus.Draft, label: 'Draft' },
  { value: ScbDealStatus.Locked, label: 'Locked' },
  { value: ScbDealStatus.Installing, label: 'Installing' },
  { value: ScbDealStatus.Live, label: 'Live' },
  { value: ScbDealStatus.Suspended, label: 'Suspended' },
  { value: ScbDealStatus.Closed, label: 'Closed' },
  { value: ScbDealStatus.Cancelled, label: 'Cancelled' },
];

export const SCB_INSTALLATION_STATUS_OPTIONS: ScbOption[] = [
  { value: ScbInstallationStatus.Planned, label: 'Planned' },
  { value: ScbInstallationStatus.InProgress, label: 'In progress' },
  { value: ScbInstallationStatus.Completed, label: 'Completed' },
  { value: ScbInstallationStatus.Cancelled, label: 'Cancelled' },
];

export const SCB_CONTRACT_STATUS_OPTIONS: ScbOption[] = [
  { value: ScbServiceContractStatus.Draft, label: 'Draft' },
  { value: ScbServiceContractStatus.Active, label: 'Active' },
  { value: ScbServiceContractStatus.PastDue, label: 'Past due' },
  { value: ScbServiceContractStatus.Grace, label: 'Grace' },
  { value: ScbServiceContractStatus.Suspended, label: 'Suspended' },
  { value: ScbServiceContractStatus.Closed, label: 'Closed' },
];

export const SCB_PLAN_LINE_STATUS_OPTIONS: ScbOption[] = [
  { value: ScbBillingPlanLineStatus.Scheduled, label: 'Scheduled' },
  { value: ScbBillingPlanLineStatus.Invoiced, label: 'Invoiced' },
  { value: ScbBillingPlanLineStatus.Sent, label: 'Sent' },
  { value: ScbBillingPlanLineStatus.PartiallyPaid, label: 'Partially paid' },
  { value: ScbBillingPlanLineStatus.Paid, label: 'Paid' },
  { value: ScbBillingPlanLineStatus.Overdue, label: 'Overdue' },
  { value: ScbBillingPlanLineStatus.Skipped, label: 'Skipped' },
  { value: ScbBillingPlanLineStatus.Cancelled, label: 'Cancelled' },
];

export const SCB_BILLING_RUN_STATUS_OPTIONS: ScbOption[] = [
  { value: ScbBillingRunStatus.Running, label: 'Running' },
  { value: ScbBillingRunStatus.Completed, label: 'Completed' },
  { value: ScbBillingRunStatus.Failed, label: 'Failed' },
];

export const SCB_BILLING_RUN_LINE_STATUS_OPTIONS: ScbOption[] = [
  { value: ScbBillingRunLineStatus.Pending, label: 'Pending' },
  { value: ScbBillingRunLineStatus.Success, label: 'Success' },
  { value: ScbBillingRunLineStatus.Failed, label: 'Failed' },
];

export const SCB_NOTIFICATION_CHANNEL_OPTIONS: ScbOption[] = [
  { value: ScbNotificationChannel.None, label: 'None' },
  { value: ScbNotificationChannel.Email, label: 'Email' },
  { value: ScbNotificationChannel.WhatsApp, label: 'WhatsApp' },
  { value: ScbNotificationChannel.Manual, label: 'Manual' },
];

export const SCB_PAYMENT_PROOF_STATUS_OPTIONS: ScbOption[] = [
  { value: ScbPaymentProofStatus.Pending, label: 'Pending' },
  { value: ScbPaymentProofStatus.Approved, label: 'Approved' },
  { value: ScbPaymentProofStatus.Rejected, label: 'Rejected' },
];

export const SCB_PAYMENT_PROOF_ROW_KIND_OPTIONS: ScbOption[] = [
  { value: ScbPaymentProofRowKind.PayInvoiceOnly, label: 'No slip' },
  { value: ScbPaymentProofRowKind.PaymentProofSubmitted, label: 'Slip uploaded' },
];

export const SCB_COST_CATEGORY_OPTIONS: ScbOption[] = [
  { value: ScbCostCategory.Install, label: 'Install' },
  { value: ScbCostCategory.Recurring, label: 'Recurring' },
  { value: ScbCostCategory.Commission, label: 'Commission' },
  { value: ScbCostCategory.Other, label: 'Other' },
];

function scbLookupLabel(options: ScbOption[], value: number | null | undefined): string {
  if (value == null) return '—';
  return options.find((o) => o.value === value)?.label ?? String(value);
}

export function scbBillingFrequencyLabel(v: number | null | undefined): string {
  return scbLookupLabel(SCB_BILLING_FREQUENCY_OPTIONS, v);
}

export function scbDealStatusLabel(v: number | null | undefined): string {
  return scbLookupLabel(SCB_DEAL_STATUS_OPTIONS, v);
}

export function scbInstallationStatusLabel(v: number | null | undefined): string {
  return scbLookupLabel(SCB_INSTALLATION_STATUS_OPTIONS, v);
}

export function scbContractStatusLabel(v: number | null | undefined): string {
  return scbLookupLabel(SCB_CONTRACT_STATUS_OPTIONS, v);
}

export function scbPlanLineStatusLabel(v: number | null | undefined): string {
  return scbLookupLabel(SCB_PLAN_LINE_STATUS_OPTIONS, v);
}

export function scbBillingRunStatusLabel(v: number | null | undefined): string {
  return scbLookupLabel(SCB_BILLING_RUN_STATUS_OPTIONS, v);
}

export function scbBillingRunLineStatusLabel(v: number | null | undefined): string {
  return scbLookupLabel(SCB_BILLING_RUN_LINE_STATUS_OPTIONS, v);
}

export function scbNotificationChannelLabel(v: number | null | undefined): string {
  return scbLookupLabel(SCB_NOTIFICATION_CHANNEL_OPTIONS, v);
}

export function scbPaymentProofStatusLabel(v: number | null | undefined): string {
  return scbLookupLabel(SCB_PAYMENT_PROOF_STATUS_OPTIONS, v);
}

export function scbCostCategoryLabel(v: number | null | undefined): string {
  return scbLookupLabel(SCB_COST_CATEGORY_OPTIONS, v);
}
